import { AlertCircle, ChevronRight, Loader2, RotateCw, Sparkles } from "lucide-react";
import Sheet from "../ui/Sheet";
import Button from "../ui/Button";
import CoverPreview from "./CoverPreview";

/**
 * The cover status strip at the top of Page photos (docs/design/HANDOFF.md,
 * "Page photos"): "Drawing your cover" while it draws, "Your cover is ready!"
 * with a View button once it's done ("Tap to see your cover" and a chevron
 * on phones), and the red "We couldn't draw your
 * cover" strip (`PhotosCoverFailed`) with Try again / Change photo when the
 * drawing failed. Nothing while there is no cover.
 */
export const CoverStatusStrip = ({ state, coverUrl, title, onView, onRetry, onChangePhoto, retrying = false, usedUp = false }) => {
  if (state === "failed") {
    return (
      <div role="alert" className="flex flex-col gap-2.5 rounded-[14px] border border-red-200 bg-white px-3.5 py-3">
        <div className="flex items-center gap-3">
          <span aria-hidden="true" className="flex h-12 w-9 flex-shrink-0 items-center justify-center rounded-md bg-red-50 text-red-700">
            <AlertCircle className="h-5 w-5" strokeWidth={2.2} />
          </span>
          <span className="flex flex-1 flex-col gap-0.5">
            <span className="text-[15px] font-bold text-md-dark">We couldn't draw your cover</span>
            <span className="text-[13px] text-md-muted">{usedUp ? "Please choose a different photo." : "Try again, or choose a different photo."}</span>
          </span>
        </div>
        <div className={usedUp ? "grid grid-cols-1" : "grid grid-cols-2 gap-2"}>
          {usedUp ? null : (
            <Button size="md" className="!min-h-[44px] !text-[15px]" onClick={onRetry} busy={retrying} icon={<RotateCw className="h-4 w-4" aria-hidden="true" />}>
              Try again
            </Button>
          )}
          <Button size="md" variant={usedUp ? "primary" : "secondary"} className="!min-h-[44px] !border !text-[15px]" onClick={onChangePhoto}>
            Change photo
          </Button>
        </div>
      </div>
    );
  }
  // Phones get a one-line 48px strip (decided 2026-10-29) so the photos start higher up; tablet and desktop keep the 64px card.
  if (state === "ready") {
    return (
      <button
        type="button"
        onClick={onView}
        className="flex min-h-[48px] w-full items-center gap-2.5 rounded-xl border border-[#E5C5CB] bg-white py-1.5 pl-1.5 pr-3 text-left text-md-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-md-pink md:min-h-[64px] md:gap-3 md:rounded-[14px] md:py-2 md:pl-2 md:pr-2.5"
      >
        <span aria-hidden="true" className="relative w-[27px] flex-shrink-0 overflow-hidden rounded md:w-9 md:rounded-[5px]">
          <CoverPreview imageUrl={coverUrl} title={title} />
        </span>
        <span className="flex-1 text-[15px] font-bold md:hidden">Tap to see your cover</span>
        <ChevronRight className="h-[18px] w-[18px] flex-shrink-0 text-md-pink-dark md:hidden" strokeWidth={2.4} aria-hidden="true" />
        <span className="hidden flex-1 flex-col gap-0.5 md:flex">
          <span className="text-[15px] font-bold">Your cover is ready!</span>
          <span className="text-[13px] text-md-muted">Tap to take a look</span>
        </span>
        <span className="hidden h-9 items-center rounded-full bg-md-pink-dark px-4 text-sm font-semibold text-white md:flex">View</span>
      </button>
    );
  }
  if (state === "making") {
    return (
      <div role="status" className="flex min-h-[48px] items-center gap-2.5 rounded-xl border border-dashed border-md-control bg-white py-1.5 pl-1.5 pr-3 md:min-h-[64px] md:gap-3 md:rounded-[14px] md:py-2 md:pl-2">
        <span aria-hidden="true" className="flex h-9 w-[27px] flex-shrink-0 items-center justify-center rounded bg-[#F1ECE5] text-md-pink-dark md:h-12 md:w-9 md:rounded-[5px]">
          <Loader2 className="h-4 w-4 animate-spin md:h-5 md:w-5" />
        </span>
        <span className="flex flex-1 flex-col gap-0.5">
          <span className="text-[15px] font-bold text-md-dark">Drawing your cover</span>
          <span className="hidden text-[13px] text-md-muted md:block">Keep adding photos. It shows up here when it's ready.</span>
        </span>
      </div>
    );
  }
  return null;
};

/**
 * The cover-ready pop-up (`PhotosCover`): the drawn cover with the title band
 * (no line of text under the heading, decided 2026-10-23),
 * "Keep adding photos" and "Use a different cover photo".
 */
export const CoverReadySheet = ({ open, onClose, coverUrl, title, onChangeCover }) => (
  <Sheet
    open={open}
    onClose={onClose}
    title="Your cover is ready!"
    actions={
      <>
        <Button size="lg" fullWidth onClick={onClose}>
          Keep adding photos
        </Button>
        <Button variant="text" fullWidth onClick={onChangeCover} className="text-md-dark">
          Use a different cover photo
        </Button>
      </>
    }
  >
    {/* The cover as it prints, with a little more room under the heading (28px). */}
    <div className="mt-3 w-[264px] flex-shrink-0 overflow-hidden rounded-xl shadow-[0_8px_24px_rgba(60,40,30,0.18)]">
      <CoverPreview
        imageUrl={coverUrl}
        title={title}
        alt="Your cover"
        placeholder={
          <span aria-hidden="true" className="flex h-full items-center justify-center text-[#6B4E44]">
            <Sparkles className="h-8 w-8" />
          </span>
        }
      />
    </div>
  </Sheet>
);

/**
 * The failed cover inside a cover-shaped box (`DeskPhotosCoverFailed` side
 * panel; also step 4's cover and Preview's cover page, decided 2026-10-23):
 * alert, "We couldn't draw your cover", Try again and Change photo. After the
 * second failed try only Change photo is left.
 */
// `hideIconOnPhones`: step 4's cover is 212 wide on phones (decided 2026-10-23), too short for the icon as well as both buttons.
export const CoverDrawFailedBox = ({ onRetry, onChangePhoto, retrying = false, usedUp = false, hideIconOnPhones = false, className = "" }) => (
  <div role="alert" className={`flex h-full w-full flex-col items-center justify-center gap-2 bg-red-50 p-4 text-center text-red-700 ${className}`}>
    <AlertCircle className={`h-7 w-7 flex-shrink-0 ${hideIconOnPhones ? "hidden md:block" : ""}`} strokeWidth={2.2} aria-hidden="true" />
    <span className="text-[15px] font-bold text-red-900">We couldn't draw your cover</span>
    {usedUp ? <span className="text-sm text-md-muted">Please choose a different photo.</span> : null}
    {usedUp ? null : (
      <Button size="sm" className="mt-1 !min-h-[38px] !text-sm" onClick={onRetry} busy={retrying} icon={<RotateCw className="h-4 w-4" aria-hidden="true" />}>
        Try again
      </Button>
    )}
    <Button variant="text" className="!text-sm" onClick={onChangePhoto}>
      Change photo
    </Button>
  </div>
);
